import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import ROUTES from '../routes/routeConstants';
import { useAppStore } from '../store/hooks';
import {
  selectHasFetchedLocations,
  selectLocationsIsLoading,
  selectLocationsLoadError
} from '../store/locations/locationsSlice';
import { useLocations } from './useLocations';

/**
 * The display name of the location a page is showing, read from the shared
 * locations slice.
 *
 * The year overview and the economics page each used to fetch the whole
 * location list inside their own load chain just to look up this one name,
 * while the header switcher had already fetched the same list through the
 * slice — so every load of those pages asked for it twice. The name comes from
 * the shared copy instead.
 *
 * Taking the list out of those load chains also took it out of their
 * error-page redirect, so that redirect lives here now: a page named after a
 * location whose name cannot be read is not worth showing. It stays an
 * initial-load-only redirect, the one thing the error page is for.
 *
 * Null while the list is still loading, and for an id the list does not
 * contain.
 */
export function useLocationName(locationId: string | undefined): string | null {
  const navigate = useNavigate();
  const store = useAppStore();
  const { locationsById, isLoading, loadError } = useLocations();

  useEffect(() => {
    // This render's values are the trigger — they re-run the effect whenever
    // the locations load changes state.
    if (isLoading || loadError === null) return;
    // The decision itself reads live state. useLocations' own effect runs
    // before this one and retries whenever the last read failed, so an error
    // this render saw is often already being retried by the time we get here —
    // redirecting on it would send the page away over a failure that the retry
    // goes on to recover from.
    const state = store.getState();
    if (selectLocationsIsLoading(state) || selectLocationsLoadError(state) === null) return;
    // Only a first load that never produced a list sends the page away. The
    // error page replaces the route, so a later failed refresh — a rename or
    // delete elsewhere in the app re-reading the list — must not throw away a
    // page the user has been editing. Once a usable list has ever arrived, a
    // failure is the header switcher's to report. See "Error surfacing" in
    // claude-docs/patterns.md.
    if (selectHasFetchedLocations(state)) return;
    navigate(`/${ROUTES.errorPage}`);
  }, [isLoading, loadError, store, navigate]);

  return locationId ? (locationsById[locationId]?.name ?? null) : null;
}
